// The sovereignty beam picture (R116, R2442).
//
// `State.beams` is a list of per-row deltas since R2442, the way the
// fleets are: a row arrives when it is new to this client or one of its
// fields moved, silence means "unchanged", and `State.beams_gone` names
// the rows that left. A full frame restates every row the viewer is
// entitled to, so it clears the picture first.
//
// A row is keyed by (beacon, source id), because a Beacon's id is a fleet
// id and every other source's is a site id, and the two id spaces can
// collide. The wire key is `source_id << 1 | beacon`; the client computes
// it by arithmetic, because a source id fills 32 bits and a JavaScript
// shift is 32 bits wide.
//
// The control layer offsets the pip phase of a beam by its source id and
// draws the rows in list order, so the picture keeps a row's place and its
// object while the row is unchanged. A changed row keeps its place too; a
// new row goes to the end.

import type { SovBeam, State } from '../gen/proto/aquaphobia';
import type { UIBeam } from '../render/control-types';
import { MPX_PER_PX } from '../world';

/** The wire key of one beam row: `source_id << 1 | (beacon ? 1 : 0)`. */
export function beamKey(beacon: boolean, sourceId: number): number {
  return sourceId * 2 + (beacon ? 1 : 0);
}

/** One wire row as the layer and the overlay read it. */
function uiBeam(b: SovBeam): UIBeam {
  return {
    sourceId: b.sourceId, beacon: b.beacon, faction: b.faction,
    x: b.xMpx / MPX_PER_PX, y: b.yMpx / MPX_PER_PX,
    targetCell: b.targetCell, progress: b.progress,
    margin10: b.margin10, against: b.against, own: b.own,
  };
}

function sameBeam(a: UIBeam, b: UIBeam): boolean {
  return a.faction === b.faction && a.x === b.x && a.y === b.y
    && a.targetCell === b.targetCell && a.progress === b.progress
    && a.margin10 === b.margin10 && a.against === b.against && a.own === b.own;
}

/**
 * The beams this client holds, applied frame by frame.
 *
 * `list` is the array the control layer and the WAR panel read. It is a
 * new array only on a frame that changed the picture, so a reader may
 * compare it by reference.
 */
export class BeamPicture {
  private readonly rows = new Map<number, UIBeam>();
  list: UIBeam[] = [];

  /** Applies one State frame. Answers true when the picture changed. */
  apply(s: State): boolean {
    let changed = false;
    if (s.full && this.rows.size > 0) {
      this.rows.clear();
      changed = true;
    }
    for (const key of s.beamsGone) changed = this.rows.delete(key) || changed;
    for (const b of s.beams) {
      const key = beamKey(b.beacon, b.sourceId);
      const row = uiBeam(b);
      const prev = this.rows.get(key);
      if (prev && sameBeam(prev, row)) continue;
      this.rows.set(key, row);
      changed = true;
    }
    if (changed) this.list = [...this.rows.values()];
    return changed;
  }

  /** Forgets every row. */
  clear(): void {
    this.rows.clear();
    this.list = [];
  }
}

/**
 * Turns frames that carry the WHOLE beam list into frames of the delta
 * meaning (R2442).
 *
 * Two producers still write whole lists: a replay recorded before
 * `BEAMS_DELTA_SINCE`, and the reel's scripted scenes. Each frame keeps its
 * rows, which the picture takes as upserts, and gains the gone keys of the
 * rows the frame before held and this one does not. A full frame needs
 * none, because the picture clears on it.
 */
export class WholeBeamList {
  private keys = new Set<number>();

  /** The frame with its `beamsGone` filled in. */
  toDelta(s: State): State {
    const now = new Set<number>();
    for (const b of s.beams) now.add(beamKey(b.beacon, b.sourceId));
    const gone: number[] = [];
    if (!s.full) for (const k of this.keys) if (!now.has(k)) gone.push(k);
    this.keys = now;
    return { ...s, beamsGone: gone };
  }

  /** Forgets the list before. A seek starts here. */
  reset(): void { this.keys.clear(); }
}
